import { useMutation } from '@tanstack/react-query';
import {
  ArrowRight,
  CheckCircle2,
  FileText,
  LinkIcon,
  Loader2,
  Send,
  Upload,
  XCircle,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  Textarea,
} from '@/components/ui';
import { cn } from '@/lib/cn';
import { pipelineApi, sourcesApi, type PasteOutcome } from '@/lib/api';

type Tab = 'text' | 'url' | 'bulk';

interface IngestResult {
  outcome: PasteOutcome;
  triggered: boolean;
  triggerError?: string;
}

/**
 * Studio /ingest — manual document ingestion. Two flows:
 *
 *   - Text paste: bypass the gate, drop the content straight into
 *     Layer 1, optionally fire the batch trigger so Layer 2 starts
 *     extracting immediately.
 *   - URL paste: registered as a manual_url source, with the same
 *     "run extraction now" affordance.
 *
 * Outcome panel surfaces the new `source_id`, the resulting status
 * (queued / ingested / rejected / fetch_failed) and a deep link into
 * the supervision page so the analyst can watch the pipeline pick
 * up the document.
 */
export function IngestPaste() {
  const { t } = useTranslation(['studio', 'common']);
  const [tab, setTab] = useState<Tab>('text');
  const [result, setResult] = useState<IngestResult | null>(null);

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-display text-brand-900">
          {t('studio:ingest.title', 'Manual ingestion')}
        </h1>
        <p className="mt-1 text-body text-slate-600">
          {t(
            'studio:ingest.subtitle',
            'Paste an article body or a URL to push a single document through the pipeline outside the normal RSS / scrape flow.',
          )}
        </p>
      </header>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap gap-1">
            <TabButton
              active={tab === 'text'}
              onClick={() => setTab('text')}
              icon={<FileText className="h-3.5 w-3.5" aria-hidden />}
              label={t('studio:ingest.tab.text', 'Paste text')}
            />
            <TabButton
              active={tab === 'url'}
              onClick={() => setTab('url')}
              icon={<LinkIcon className="h-3.5 w-3.5" aria-hidden />}
              label={t('studio:ingest.tab.url', 'Paste URL')}
            />
            <TabButton
              active={tab === 'bulk'}
              onClick={() => setTab('bulk')}
              icon={<Upload className="h-3.5 w-3.5" aria-hidden />}
              label={t('studio:ingest.tab.bulk', 'Bulk URLs')}
            />
          </div>
        </CardHeader>
        <CardContent>
          {tab === 'text' && <TextForm onResult={setResult} />}
          {tab === 'url' && <UrlForm onResult={setResult} />}
          {tab === 'bulk' && <BulkUrlsForm />}
        </CardContent>
      </Card>

      {result && <ResultPanel result={result} onClear={() => setResult(null)} />}
    </div>
  );
}

// ─── Text form ─────────────────────────────────────────────────

function TextForm({ onResult }: { onResult: (r: IngestResult) => void }) {
  const { t } = useTranslation(['studio', 'common']);
  const [sourceUrl, setSourceUrl] = useState('');
  const [contentText, setContentText] = useState('');
  const [sourceAttribution, setSourceAttribution] = useState('');
  const [topicTagsRaw, setTopicTagsRaw] = useState('');
  const [publicationDate, setPublicationDate] = useState('');
  const [language, setLanguage] = useState('');
  const [runNow, setRunNow] = useState(true);

  const mutation = useMutation({
    mutationFn: async () => {
      const tags = parseTags(topicTagsRaw);
      const trimmedUrl = sourceUrl.trim();
      const outcome = await sourcesApi.pasteText({
        // Backend auto-generates a `urn:ett:paste:<uuid>` when the
        // analyst doesn't have a real URL to attach.
        ...(trimmedUrl ? { source_url: trimmedUrl } : {}),
        content_text: contentText,
        source_attribution: sourceAttribution.trim(),
        topic_tags: tags,
        ...(publicationDate ? { publication_date: publicationDate } : {}),
        ...(language ? { language: language.trim().toLowerCase() } : {}),
      });
      let triggered = false;
      let triggerError: string | undefined;
      if (runNow && outcome.success) {
        try {
          await pipelineApi.trigger();
          triggered = true;
        } catch (err) {
          triggerError = err instanceof Error ? err.message : String(err);
        }
      }
      return { outcome, triggered, triggerError };
    },
    onSuccess: onResult,
  });

  const wordCount = contentText.trim() ? contentText.trim().split(/\s+/).length : 0;
  const canSubmit =
    contentText.trim().length > 0 &&
    sourceAttribution.trim().length > 0 &&
    !mutation.isPending;

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) mutation.mutate();
      }}
    >
      <Field
        label={t(
          'studio:ingest.field.sourceUrlOptional',
          'Source URL (optional — auto-generated if blank)',
        )}
      >
        <Input
          type="url"
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.target.value)}
          placeholder="https://example.com/article-or-original-source"
        />
      </Field>

      <Field
        label={
          <span className="flex items-baseline justify-between gap-2">
            <span>{t('studio:ingest.field.content', 'Content')}</span>
            <span className="text-[10px] text-slate-400">
              {t('studio:ingest.wordCount', { n: wordCount, defaultValue: '{{n}} words' })}
            </span>
          </span>
        }
        required
      >
        <Textarea
          rows={10}
          value={contentText}
          onChange={(e) => setContentText(e.target.value)}
          placeholder={t(
            'studio:ingest.contentPlaceholder',
            'Paste the article body — markdown, plain text or HTML all accepted.',
          )}
          required
        />
      </Field>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={t('studio:ingest.field.attribution', 'Attribution / author')} required>
          <Input
            value={sourceAttribution}
            onChange={(e) => setSourceAttribution(e.target.value)}
            placeholder="e.g. Reuters Energy, Jane Doe on LinkedIn"
            required
          />
        </Field>
        <Field
          label={t(
            'studio:ingest.field.tagsOptional',
            'Topic tags (optional — pipeline will infer from content)',
          )}
        >
          <Input
            value={topicTagsRaw}
            onChange={(e) => setTopicTagsRaw(e.target.value)}
            placeholder="hydrogen, offshore_wind"
          />
        </Field>
        <Field label={t('studio:ingest.field.pubDate', 'Publication date (optional)')}>
          <Input
            type="date"
            value={publicationDate}
            onChange={(e) => setPublicationDate(e.target.value)}
          />
        </Field>
        <Field label={t('studio:ingest.field.language', 'Language (ISO 639-1, optional)')}>
          <Input
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            placeholder="en"
            maxLength={2}
          />
        </Field>
      </div>

      <RunNowToggle checked={runNow} onChange={setRunNow} />
      <SubmitRow
        disabled={!canSubmit}
        pending={mutation.isPending}
        error={mutation.error}
        runNow={runNow}
      />
    </form>
  );
}

// ─── URL form ──────────────────────────────────────────────────

function UrlForm({ onResult }: { onResult: (r: IngestResult) => void }) {
  const { t } = useTranslation(['studio', 'common']);
  const [sourceUrl, setSourceUrl] = useState('');
  const [sourceAttribution, setSourceAttribution] = useState('');
  const [topicTagsRaw, setTopicTagsRaw] = useState('');
  const [runNow, setRunNow] = useState(true);

  const mutation = useMutation({
    mutationFn: async () => {
      // `backfill_session` fetches inline so the analyst gets immediate
      // feedback on the gate result; `daily_queue` would defer to cron.
      const trimmedAttribution = sourceAttribution.trim();
      const outcome = await sourcesApi.pasteUrl({
        source_url: sourceUrl.trim(),
        // Backend falls back to the URL hostname when missing — keep
        // the request small by omitting empty optionals.
        ...(trimmedAttribution ? { source_attribution: trimmedAttribution } : {}),
        topic_tags: parseTags(topicTagsRaw),
        mode: 'backfill_session',
      });
      let triggered = false;
      let triggerError: string | undefined;
      if (runNow && outcome.success) {
        try {
          await pipelineApi.trigger();
          triggered = true;
        } catch (err) {
          triggerError = err instanceof Error ? err.message : String(err);
        }
      }
      return { outcome, triggered, triggerError };
    },
    onSuccess: onResult,
  });

  const canSubmit = sourceUrl.trim().length > 0 && !mutation.isPending;

  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) mutation.mutate();
      }}
    >
      <Field label={t('studio:ingest.field.urlToFetch', 'URL to fetch')} required>
        <Input
          type="url"
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.target.value)}
          placeholder="https://example.com/article"
          required
        />
      </Field>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label={t(
            'studio:ingest.field.attributionOptional',
            'Attribution / author (optional — defaults to URL host)',
          )}
        >
          <Input
            value={sourceAttribution}
            onChange={(e) => setSourceAttribution(e.target.value)}
            placeholder="e.g. Reuters Energy"
          />
        </Field>
        <Field
          label={t(
            'studio:ingest.field.tagsOptional',
            'Topic tags (optional — pipeline will infer from content)',
          )}
        >
          <Input
            value={topicTagsRaw}
            onChange={(e) => setTopicTagsRaw(e.target.value)}
            placeholder="hydrogen, offshore_wind"
          />
        </Field>
      </div>

      <p className="text-small text-slate-500">
        {t(
          'studio:ingest.urlMode',
          'URL paste runs fetch + gate inline (backfill mode) so you get immediate feedback on whether the source was accepted.',
        )}
      </p>

      <RunNowToggle checked={runNow} onChange={setRunNow} />
      <SubmitRow
        disabled={!canSubmit}
        pending={mutation.isPending}
        error={mutation.error}
        runNow={runNow}
      />
    </form>
  );
}

// ─── Bulk URLs form ────────────────────────────────────────────

type RowStatus = 'pending' | 'queueing' | 'queued' | 'error';
interface BulkRow {
  url: string;
  status: RowStatus;
  source_id?: string;
  error?: string;
}

const BULK_CONCURRENCY = 4;

function BulkUrlsForm() {
  const { t } = useTranslation(['studio', 'common']);
  const [raw, setRaw] = useState('');
  const [attribution, setAttribution] = useState('');
  const [topicTagsRaw, setTopicTagsRaw] = useState('');
  const [runNow, setRunNow] = useState(true);
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [running, setRunning] = useState(false);
  const [triggered, setTriggered] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const detectedUrls = useMemo(() => extractUrls(raw), [raw]);

  const onFile = async (file: File) => {
    const text = await file.text();
    // Append parsed content so the analyst can review before submitting.
    setRaw((prev) => (prev ? `${prev}\n${text}` : text));
  };

  const submit = async () => {
    if (detectedUrls.length === 0 || running) return;
    setRunning(true);
    setTriggered(false);
    const initial: BulkRow[] = detectedUrls.map((url) => ({ url, status: 'pending' }));
    setRows(initial);

    const tags = parseTags(topicTagsRaw);
    const trimmedAttr = attribution.trim();

    // Worker pool: process URLs with a small concurrency so we don't
    // overwhelm the backend (each pasteUrl writes a Postgres row +
    // enqueues an arq job).
    let cursor = 0;
    const worker = async () => {
      while (cursor < detectedUrls.length) {
        const i = cursor++;
        const url = detectedUrls[i]!;
        setRows((prev) =>
          prev.map((r, idx) => (idx === i ? { ...r, status: 'queueing' } : r)),
        );
        try {
          const outcome = await sourcesApi.pasteUrl({
            source_url: url,
            ...(trimmedAttr ? { source_attribution: trimmedAttr } : {}),
            topic_tags: tags,
            mode: 'daily_queue',
          });
          setRows((prev) =>
            prev.map((r, idx) =>
              idx === i
                ? {
                    ...r,
                    status: outcome.success ? 'queued' : 'error',
                    source_id: outcome.source_id ?? undefined,
                    ...(outcome.success ? {} : { error: outcome.status }),
                  }
                : r,
            ),
          );
        } catch (err) {
          setRows((prev) =>
            prev.map((r, idx) =>
              idx === i
                ? {
                    ...r,
                    status: 'error',
                    error: err instanceof Error ? err.message : String(err),
                  }
                : r,
            ),
          );
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(BULK_CONCURRENCY, detectedUrls.length) }, worker),
    );

    if (runNow) {
      try {
        await pipelineApi.trigger();
        setTriggered(true);
      } catch {
        // Non-fatal — the queued docs will be picked up by the next cron tick.
      }
    }
    setRunning(false);
  };

  const counts = useMemo(() => {
    const c = { pending: 0, queueing: 0, queued: 0, error: 0 };
    for (const r of rows) c[r.status]++;
    return c;
  }, [rows]);

  return (
    <div className="space-y-3">
      <p className="text-small text-slate-500">
        {t(
          'studio:ingest.bulk.help',
          'Paste a list of URLs (one per line) or drop a .csv / .txt file. From Excel, copy the URL column and paste — Excel separates cells with newlines.',
        )}
      </p>

      <Field
        label={
          <span className="flex items-baseline justify-between gap-2">
            <span>
              {t('studio:ingest.bulk.urls', 'URLs (one per line, or any separator)')}
            </span>
            <span className="text-[10px] text-slate-400">
              {t('studio:ingest.bulk.detected', {
                n: detectedUrls.length,
                defaultValue: '{{n}} URLs detected',
              })}
            </span>
          </span>
        }
      >
        <Textarea
          rows={8}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder={'https://example.com/article-1\nhttps://example.com/article-2'}
          spellCheck={false}
          className="font-mono text-small"
        />
      </Field>

      <div className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.txt,text/csv,text/plain"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onFile(f);
            e.target.value = '';
          }}
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={running}
        >
          <Upload className="mr-1 h-3.5 w-3.5" aria-hidden />
          {t('studio:ingest.bulk.upload', 'Upload .csv / .txt')}
        </Button>
        <span className="text-small text-slate-500">
          {t(
            'studio:ingest.bulk.uploadHint',
            'For .xlsx: copy the URL column in Excel, then paste above.',
          )}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label={t(
            'studio:ingest.field.attributionOptional',
            'Attribution / author (optional)',
          )}
        >
          <Input
            value={attribution}
            onChange={(e) => setAttribution(e.target.value)}
            placeholder="Applied to every URL — e.g. Reuters Energy"
          />
        </Field>
        <Field
          label={t(
            'studio:ingest.field.tagsOptional',
            'Topic tags (optional)',
          )}
        >
          <Input
            value={topicTagsRaw}
            onChange={(e) => setTopicTagsRaw(e.target.value)}
            placeholder="hydrogen, offshore_wind"
          />
        </Field>
      </div>

      <RunNowToggle checked={runNow} onChange={setRunNow} />

      <Button
        type="button"
        onClick={() => void submit()}
        disabled={detectedUrls.length === 0 || running}
        className="w-full"
      >
        {running ? (
          <>
            <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
            {t('studio:ingest.bulk.queueing', {
              done: counts.queued + counts.error,
              total: detectedUrls.length,
              defaultValue: 'Queueing {{done}}/{{total}}…',
            })}
          </>
        ) : (
          <>
            <Send className="mr-1 h-4 w-4" aria-hidden />
            {t('studio:ingest.bulk.submit', {
              n: detectedUrls.length,
              defaultValue: 'Queue {{n}} URL(s)',
            })}
          </>
        )}
      </Button>

      {rows.length > 0 && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-small">
            <Badge variant="success">{counts.queued} queued</Badge>
            {counts.error > 0 && <Badge variant="error">{counts.error} failed</Badge>}
            {counts.queueing > 0 && (
              <Badge variant="neutral">{counts.queueing} in flight</Badge>
            )}
            {counts.pending > 0 && (
              <Badge variant="neutral">{counts.pending} pending</Badge>
            )}
            {triggered && (
              <span className="text-green-700">
                {t('studio:ingest.bulk.triggered', 'Batch trigger fired')}
              </span>
            )}
          </div>
          <ul className="max-h-72 divide-y divide-slate-100 overflow-y-auto rounded border border-slate-200 font-mono text-small">
            {rows.map((row) => (
              <li
                key={row.url + row.status}
                className="flex items-center gap-2 px-2 py-1"
              >
                <StatusDot status={row.status} />
                <span className="flex-1 truncate" title={row.url}>
                  {row.url}
                </span>
                {row.error && (
                  <span className="shrink-0 text-red-600">{row.error}</span>
                )}
                {row.source_id && (
                  <span
                    className="shrink-0 text-[10px] text-slate-500"
                    title={row.source_id}
                  >
                    {row.source_id.slice(0, 8)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatusDot({ status }: { status: RowStatus }) {
  const dot: Record<RowStatus, string> = {
    pending: 'bg-slate-300',
    queueing: 'bg-amber-400 animate-pulse',
    queued: 'bg-emerald-500',
    error: 'bg-red-500',
  };
  return (
    <span
      aria-hidden
      className={cn('inline-block h-2 w-2 shrink-0 rounded-full', dot[status])}
    />
  );
}

/** Extract every http(s) URL from a freeform blob. Deduplicates + preserves order. */
function extractUrls(raw: string): string[] {
  if (!raw.trim()) return [];
  const urlRegex = /https?:\/\/[^\s,;"'<>()\]\[]+/g;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const match of raw.matchAll(urlRegex)) {
    let url = match[0];
    // Trim trailing punctuation often pasted alongside (.,;)
    url = url.replace(/[.,;:!?]+$/, '');
    if (!seen.has(url)) {
      seen.add(url);
      out.push(url);
    }
  }
  return out;
}

// ─── Shared form bits ──────────────────────────────────────────

function Field({
  label,
  required,
  children,
}: {
  label: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-0.5 block text-h4 text-slate-700">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded px-3 py-1.5 text-small transition-colors',
        active
          ? 'bg-brand-50 font-medium text-brand-900'
          : 'text-slate-600 hover:bg-slate-50',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function RunNowToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const { t } = useTranslation('studio');
  return (
    <label className="flex items-start gap-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-small text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5"
      />
      <span>
        <strong>{t('ingest.runNow', 'Run extraction now')}</strong>
        <br />
        <span className="text-slate-500">
          {t(
            'ingest.runNowHint',
            'Fires the nightly batch trigger right after ingestion so Layer 2 extraction starts immediately. Unchecked = wait for the next scheduled run.',
          )}
        </span>
      </span>
    </label>
  );
}

function SubmitRow({
  disabled,
  pending,
  error,
  runNow,
}: {
  disabled: boolean;
  pending: boolean;
  error: unknown;
  runNow: boolean;
}) {
  const { t } = useTranslation(['studio', 'common']);
  return (
    <div className="space-y-2">
      <Button type="submit" disabled={disabled} className="w-full">
        {pending ? (
          <>
            <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
            {t('studio:ingest.submitting', 'Ingesting…')}
          </>
        ) : (
          <>
            <Send className="mr-1 h-4 w-4" aria-hidden />
            {runNow
              ? t('studio:ingest.submitRun', 'Ingest + run extraction')
              : t('studio:ingest.submit', 'Ingest')}
          </>
        )}
      </Button>
      {error instanceof Error && (
        <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
          {error.message}
        </p>
      )}
    </div>
  );
}

// ─── Outcome ───────────────────────────────────────────────────

function ResultPanel({
  result,
  onClear,
}: {
  result: IngestResult;
  onClear: () => void;
}) {
  const { t } = useTranslation(['studio', 'common']);
  const { outcome, triggered, triggerError } = result;
  const accent = outcome.success ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/50';
  const Icon = outcome.success ? CheckCircle2 : XCircle;
  const iconColor = outcome.success ? 'text-green-600' : 'text-red-600';

  return (
    <Card className={cn('border', accent)}>
      <CardHeader>
        <div className="flex items-start gap-2">
          <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', iconColor)} aria-hidden />
          <div className="flex-1">
            <h2 className="text-h2 text-brand-900">
              {outcome.success
                ? t('studio:ingest.result.title', 'Document ingested')
                : t('studio:ingest.result.failedTitle', 'Ingestion failed')}
            </h2>
            <p className="text-small text-slate-600">
              <Badge variant="neutral" className="mr-1">
                {outcome.input_type}
              </Badge>
              <Badge variant="neutral" className="mr-1">
                {outcome.mode}
              </Badge>
              <span>status: {outcome.status}</span>
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClear}>
            {t('common:actions.dismiss', 'Dismiss')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-small">
        {outcome.source_id && (
          <p>
            <span className="text-slate-500">source_id:</span>{' '}
            <span className="font-mono text-slate-700">{outcome.source_id}</span>
          </p>
        )}
        {triggered && (
          <p className="text-green-700">
            {t(
              'studio:ingest.result.triggered',
              'Batch trigger fired — the worker is processing the queue.',
            )}
          </p>
        )}
        {triggerError && (
          <p className="text-red-700">
            {t('studio:ingest.result.triggerFailed', {
              error: triggerError,
              defaultValue: 'Batch trigger failed: {{error}}',
            })}
          </p>
        )}
        {outcome.success && (
          <div className="flex flex-wrap gap-2 pt-1">
            <Button asChild size="sm" variant="secondary">
              <Link to="/studio/pipeline-runs">
                {t('studio:ingest.result.linkSupervision', 'Open pipeline supervision')}
                <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden />
              </Link>
            </Button>
            <Button asChild size="sm" variant="ghost">
              <Link to="/studio/errors">
                {t('studio:ingest.result.linkErrors', 'Open error queue')}
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Helpers ───────────────────────────────────────────────────

function parseTags(raw: string): string[] {
  return raw
    .split(/[,\s]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0);
}
